import type { AccountInfo } from '@azure/msal-browser';
import { startInteraction } from './interaction-lock';
import { environment } from '../../environments/environment';

/**
 * Entra scope for the Vericure API.
 *
 * ONE app registration, not two. 'VeriCure'
 * (7371635d-af32-439b-8a9a-0e6038e5c6b3) is both the SPA the user signs into AND
 * the API being called: it exposes its own `access_as_user` and is
 * self-pre-authorized, so the token comes back with an audience equal to the app
 * the user already authenticated against.
 *
 * WHY THIS CHANGED. The scope used to point at a separate 'Vericure API'
 * registration (b308409b-52b8-4b45-a5aa-543be636b569). That works inside
 * vericure.io, but a guest tenant signing in cross-tenant has no service
 * principal for that second app, and provisioning one per customer tenant is the
 * step that kept blocking onboarding. A single self-contained app removes the
 * second resource entirely — there is nothing extra for a customer tenant to
 * consent to or have provisioned.
 *
 * NOTE the appId below is deliberately identical to the clientId in app.module.
 * That is the point of the consolidation, not a copy-paste error: same app, both
 * roles.
 *
 * Held here rather than in each environment file so the value cannot drift
 * between configs — one registration across all environments; only the API host
 * differs, and that already comes from `environment.apiUrl`.
 */
// RE-LANDED: VeriCure's own scope. Same value 866 used; what changed is
// everything around it.
//
// 866 failed because a session predating the cutover had no grant for the
// newly-exposed scope, acquireTokenSilent threw interaction_required, and the
// interceptor swallowed it and sent no bearer — silently and permanently. That
// took beta-dev data down for every user and was rolled back in 871.
//
// What is different now:
//   - The scope is admin-consented in the vortex tenant (SP, Graph User.Read and
//     access_as_user, all AllPrincipals), so a guest tenant no longer needs a
//     service principal for a second app — the reason for consolidating.
//   - DataServices dual-accepts 7371635d and b308409b, so tokens are honoured on
//     every data endpoint during the transition.
//   - 873 gives a stranded session a way out: the interceptor waits for MSAL to
//     settle, and a genuine interaction_required now triggers ONE redirect that
//     returns the user to the same project and tab, rather than a silent 403.
//
// The appId below is deliberately identical to the clientId in app.module. That
// is the point of the consolidation — same app, both roles — not a copy-paste
// error, and it looks enough like one to be worth saying twice.
// Per-environment override, same defensive-read pattern as requireEntraAuth
// below (see environment.prod2.entraClientId) — MUST stay in lockstep with
// app.module.ts's MSALInstanceFactory clientId, since the token's audience has
// to match the app the user actually signed into. Environments that don't
// declare entraClientId (dev, beta-dev, local) fall back to the shared app,
// unaffected by this.
export const VERICURE_API_APP_ID =
  (environment as { entraClientId?: string }).entraClientId ??
  // The TEST app, not the production one. Five configs (development, local,
  // local-beta, local-4300, winbackend) declare no entraClientId and land here,
  // and every one of them is a lower environment — two of them (local-beta,
  // local-4300) talk to the beta-dev API with requireEntraAuth on. A fallback
  // that pointed at the PRODUCTION app would hand a local dev build a prod
  // identity by omission, which is the wrong way for a default to fail.
  '2e945b52-de34-4243-a63c-b68cb91c662b';

// BARE GUID, not the api:// form.
//
// Both are legal scope syntaxes and which one works depends on the app
// registration's Application ID URI. These apps expose <appId>/access_as_user,
// so requesting api://<appId>/access_as_user would come back invalid_scope —
// and api-scope deliberately does NOT redirect on invalid_scope, so it would
// surface as refused requests rather than anything that looks like an auth
// prompt.
export const VERICURE_API_SCOPE = `${VERICURE_API_APP_ID}/access_as_user`;

/** Scopes for a Microsoft Graph token. Kept separate — see requireEntraAuth(). */
export const GRAPH_SCOPES = ['User.Read'];

/**
 * Client-side half of the server's RequireEntraAuth switch.
 *
 * While this is false the client behaves exactly as before: VericureDataServices
 * calls authenticate with the function key (`?code=`) only, and MSAL is never asked
 * for an API token. When the server turns RequireEntraAuth on, flipping this makes
 * MsalInterceptor attach a bearer with the correct audience to those same calls —
 * the function-key query parameter is still sent, so the two paths overlap and the
 * cutover needs no lockstep deploy.
 *
 * Read defensively: not every environment config declares the flag (the local
 * `winbackend` config is developer-owned and untracked), and a missing flag must
 * mean "off", not a compile error.
 */
export function requireEntraAuth(): boolean {
  return (environment as { requireEntraAuth?: boolean }).requireEntraAuth === true;
}

/**
 * Base URL that MsalInterceptor matches VericureDataServices calls against.
 *
 * Comes from `environment.apiUrl`, so each build targets its own API host — the
 * beta-dev build gets the beta-dev Function App, prod gets prod. Hardcoding one
 * host here would attach the wrong audience (or none) in the other configs.
 */
export function apiResourceUrl(): string {
  return environment.apiUrl;
}

/**
 * Scopes to request at INTERACTIVE sign-in.
 *
 * This is the only place the API scope can be consented and cached. MSAL's
 * `MSALGuardConfigFactory.authRequest` does NOT apply to the app's login call:
 * MsalGuard is not `canActivate` on any route (routes use `userAuthGuard`), and
 * `UserAuthService.login()` does not spread `msalGuardConfig.authRequest`. So a
 * scope that is not in THIS list is never consented at sign-in, and the
 * interceptor's later `acquireTokenSilent` for it throws `interaction_required`
 * and falls back to the function key.
 *
 * Requesting the API scope here costs nothing when the SPA is pre-authorized on
 * the API app registration — no extra consent prompt — and it means the token is
 * already in the MSAL cache before the first data call.
 */
export function loginScopes(): string[] {
  return requireEntraAuth()
    ? [...GRAPH_SCOPES, VERICURE_API_SCOPE]
    : [...GRAPH_SCOPES];
}

/**
 * MSAL error codes that mean "silent acquisition cannot succeed; only an
 * interactive round trip can fix this".
 *
 * `interaction_required` and `login_required` are the classic silent-iframe
 * failures. `consent_required` means the scope needs an explicit grant.
 * `invalid_scope` is deliberately NOT here: that is a configuration fault (wrong
 * Application ID URI, or the scope not exposed) and redirecting the user would
 * loop without ever fixing it — it must surface as an error instead.
 */
const INTERACTION_ERROR_CODES = [
  'interaction_required',
  'login_required',
  'consent_required',
];

export function isInteractionRequired(error: unknown): boolean {
  const code =
    (error as { errorCode?: string })?.errorCode ??
    (error as { message?: string })?.message ??
    '';
  return INTERACTION_ERROR_CODES.some((c) => String(code).includes(c));
}

/**
 * Bounded, per-account attempt budget for the API-scope recovery redirect.
 *
 * THIS USED TO BE A SESSION-WIDE BOOLEAN, and that is what turned a recoverable
 * failure into a dead session. The first `interaction_required` set the flag; every
 * later failure — including the one at login, the moment a redirect is most likely
 * to actually work — logged "staying on the function-key path" and gave up. There
 * is no function key any more, so "giving up" now means a keyless request and a
 * 401. The only exit was clearing sessionStorage by hand.
 *
 * It stays BOUNDED, deliberately. The redirect genuinely can fail to repair the
 * grant (misconfigured Application ID URI, a tenant that will not consent), and an
 * unbounded retry is an infinite redirect loop on every page load — strictly worse
 * than the bug it replaces. So: a COUNT, not a flag. A small budget per account,
 * reset to zero the moment an acquisition succeeds.
 */
export const API_SCOPE_REDIRECT_KEY = 'vericureApiScopeRedirected';

/** Attempts allowed per account before we stop and surface the failure instead. */
export const API_SCOPE_MAX_REDIRECTS = 2;

/**
 * Budget is per ACCOUNT, not per session.
 *
 * A browser holding two identities (say a home tenant and a guest tenant) would
 * otherwise let a hopeless account burn the budget belonging to a recoverable one.
 */
function attemptKey(account?: AccountInfo): string {
  const home = account?.homeAccountId;
  return home ? `${API_SCOPE_REDIRECT_KEY}:${home}` : API_SCOPE_REDIRECT_KEY;
}

function readAttempts(key: string): number {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) {
      return 0;
    }
    // '1' is the OLD boolean latch. Reading it as "one attempt already spent"
    // means a session that upgrades into this build mid-flight is not handed a
    // fresh budget on top of the redirect it has already been through.
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 1;
  } catch {
    // Storage blocked (private mode). Treat as no attempts spent; the bound is
    // best effort and the worst case is one extra redirect.
    return 0;
  }
}

function writeAttempts(key: string, value: number): void {
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    // best effort
  }
}

/**
 * Send the user to Microsoft to re-establish the API scope, and BRING THEM BACK.
 *
 * `redirectStartPage` is the whole point. Without it MSAL returns to the app's
 * configured redirectUri — the generic landing page — so a user who was three
 * clicks into a project, on a specific tab, following a deep link, comes back to
 * a picker with no idea what happened. Passing the current href means the round
 * trip is invisible apart from the Microsoft page itself: same project, same tab,
 * same query string.
 *
 * PASS THE ACCOUNT. Without it MSAL issues the authorize request with no login
 * hint against the `/common` authority, so it re-establishes a grant for whichever
 * identity the browser's Microsoft session happens to favour — which need not be
 * the account whose silent acquisition just failed. The token lands under a
 * different account, the original lookup still misses, and the next attempt fails
 * identically. That is a redirect that cannot succeed no matter how many times it
 * re-fires, which is why re-firing alone was never the whole fix.
 *
 * Returns true if a redirect was started, so callers know the page is going away.
 */
export function redirectForApiScope(
  instance: {
    acquireTokenRedirect: (req: {
      scopes: string[];
      redirectStartPage?: string;
      // AccountInfo, not unknown: MSAL's own RedirectRequest types this field, and
      // a looser structural type here makes IPublicClientApplication unassignable
      // to this parameter at every call site.
      account?: AccountInfo;
      prompt?: string;
    }) => Promise<void> | void;
  },
  reason: string,
  account?: AccountInfo
): boolean {
  // Gated on requireEntraAuth, not on a switch of its own.
  //
  // This USED TO have a separate `entraInteractiveFallback` flag. Every shipped
  // config set the two together, but nothing enforced that, and the unpaired
  // combination was live-but-unreachable nonsense: with auth OFF the app needs
  // no API token at all, yet a silent-acquisition failure would still have sent
  // the user on a full-page redirect to consent a scope nothing was going to
  // use. Deriving it removes the hazard rather than documenting it.
  if (!requireEntraAuth()) {
    return false;
  }

  const key = attemptKey(account);
  const spent = readAttempts(key);
  if (spent >= API_SCOPE_MAX_REDIRECTS) {
    console.warn(
      `Vericure API scope still unavailable after ${spent} interactive ` +
        `attempt(s) (${reason}). Not redirecting again — an unbounded retry is a ` +
        `redirect loop. The request will be refused client-side instead of going ` +
        `out unauthenticated; signing in again is what clears this.`
    );
    return false;
  }
  writeAttempts(key, spent + 1);

  let startPage = '';
  try {
    startPage = globalThis.location?.href ?? '';
  } catch {
    startPage = '';
  }

  console.warn(
    `Redirecting to re-establish the Vericure API scope (${reason}); attempt ` +
      `${spent + 1}/${API_SCOPE_MAX_REDIRECTS}; returning to ` +
      `${startPage || 'the default page'}`
  );
  // This used to catch-and-warn, which turned a RECOVERABLE stuck flag into a
  // dead end: the budget was already spent by then, so the UI reported
  // "signing in again did not restore it" for a condition one sessionStorage
  // delete would have fixed.
  return startInteraction(
    () =>
      instance.acquireTokenRedirect({
        scopes: [VERICURE_API_SCOPE],
        ...(startPage ? { redirectStartPage: startPage } : {}),
        // With an account, MSAL anchors the authorize request to that identity's
        // session. WITHOUT one we must not let it pick: an unhinted request against
        // /common silently re-grants to whichever Microsoft session the browser
        // prefers, which is precisely how the wrong account got anchored. Asking is
        // the only correct move when we genuinely do not know.
        ...(account ? { account } : { prompt: 'select_account' }),
      }),
    `acquireTokenRedirect (${reason})`
  );
}

/**
 * Clear the budget — call after a successful acquisition.
 *
 * Clears EVERY per-account key, not just the bare one, so a session that recovers
 * gets a full budget again for a later, unrelated expiry.
 */
export function clearApiScopeRedirectLatch(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(API_SCOPE_REDIRECT_KEY)) {
        doomed.push(k);
      }
    }
    doomed.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // best effort
  }
}
